import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Check, ChevronDown, ChevronRight, Save } from "lucide-react";
import { ROLES } from "@/lib/constants";

const NAVIGATION_PERMISSION_REQUIREMENTS = {
  tickets: "view_tickets",
  reports: "view_tickets",
  new_ticket: "create_tickets",
  admin_users: "manage_users",
  admin_applications: "manage_applications",
  admin_sla: "manage_sla",
  admin_smtp: "configure_smtp",
  admin_whatsapp: "configure_whatsapp",
  admin_notification_logs: "view_notification_logs",
  admin_audit: "view_audit",
};

export default function RolesMatrixPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [edited, setEdited] = useState({}); // { role -> {permissions, navigation, responsibility} }
  const [saving, setSaving] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({});

  const load = () => {
    api.get("/permissions").then((r) => {
      setData(r.data);
      setEdited({});
    });
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-muted-foreground">Loading roles…</div>;
  const roles = Object.keys(data.roles);
  const canEdit = user?.role === ROLES.SUPER_ADMIN;

  const permsFor = (role) => {
    const overridden = edited[role]?.permissions;
    return overridden || new Set(data.roles[role]);
  };
  const respFor = (role) => {
    if (edited[role]?.responsibility !== undefined) return edited[role].responsibility;
    return data.responsibilities[role] || "";
  };
  const navigationFor = (role) => {
    const allowed = new Set(data.available_navigation?.[role] || []);
    const configured = edited[role]?.navigation || new Set(data.role_navigation?.[role] || []);
    return new Set(Array.from(configured).filter((item) => allowed.has(item)));
  };
  const setNavigation = (role, itemId, checked) => {
    const available = new Set(data.available_navigation?.[role] || []);
    const navItems = new Map((data.navigation_items || []).map((item) => [item.id, item]));
    const administration = navItems.get("administration");
    const children = (administration?.children || [])
      .map((item) => item.id)
      .filter((id) => available.has(id));
    setEdited((prev) => {
      const selected = new Set(prev[role]?.navigation || navigationFor(role));
      const permissions = new Set(prev[role]?.permissions || data.roles[role]);
      let grantsPermission = false;
      if (itemId === "administration") {
        if (checked) children.forEach((id) => selected.add(id));
        else children.forEach((id) => selected.delete(id));
      } else if (checked) selected.add(itemId);
      else selected.delete(itemId);
      const newlySelected = itemId === "administration" && checked
        ? children
        : checked ? [itemId] : [];
      newlySelected.forEach((id) => {
        const requiredPermission = NAVIGATION_PERMISSION_REQUIREMENTS[id];
        if (role !== ROLES.SUPER_ADMIN && requiredPermission && (data.available?.[role] || []).includes(requiredPermission)) {
          permissions.add(requiredPermission);
          grantsPermission = true;
        }
      });
      if (children.some((id) => selected.has(id))) selected.add("administration");
      else selected.delete("administration");
      return {
        ...prev,
        [role]: {
          ...(prev[role] || {}),
          navigation: selected,
          ...(grantsPermission ? { permissions } : {}),
        },
      };
    });
  };
  const setPerm = (role, key, has) => {
    setEdited((prev) => {
      const cur = new Set(prev[role]?.permissions || data.roles[role]);
      if (has) cur.add(key); else cur.delete(key);
      return { ...prev, [role]: { ...(prev[role] || {}), permissions: cur } };
    });
  };
  const setResp = (role, text) => {
    setEdited((prev) => ({ ...prev, [role]: { ...(prev[role] || {}), responsibility: text } }));
  };

  const save = async (role) => {
    setSaving(role);
    try {
      const payload = {};
      if (edited[role]?.permissions) payload.permissions = Array.from(edited[role].permissions);
      if (edited[role]?.responsibility !== undefined) payload.responsibility = edited[role].responsibility;
      if (edited[role]?.navigation) payload.navigation = Array.from(edited[role].navigation);
      if (Object.keys(payload).length === 0) return;
      await api.put(`/roles/${role}`, payload);
      toast.success(`Role settings updated for ${role.replace(/_/g, " ")}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-roles-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Administration</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Roles &amp; permissions</h1>
        <p className="text-sm text-muted-foreground">
          {canEdit
            ? "Edit each role's permissions and responsibility description. Permissions outside a role's ticket or administration scope are read-only. Changes are written to the audit log."
            : "View each role's permissions and responsibility descriptions. Only Super Admin can edit."}
        </p>
      </div>

      {/* Responsibility cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {roles.map((r) => (
          <div key={r} data-testid={`role-card-${r}`} className="rounded-xl border border-border bg-card p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-primary">{r.replace(/_/g, " ")}</div>
              {canEdit && edited[r]?.responsibility !== undefined && (
                <Button size="sm" variant="outline" data-testid={`role-resp-save-${r}`} onClick={() => save(r)} disabled={saving === r}>
                  <Save className="h-3 w-3 mr-1" /> Save
                </Button>
              )}
            </div>
            {canEdit ? (
              <Textarea
                data-testid={`role-resp-${r}`}
                value={respFor(r)}
                onChange={(e) => setResp(r, e.target.value)}
                rows={3}
                className="text-sm"
              />
            ) : (
              <div className="text-sm text-muted-foreground">{respFor(r)}</div>
            )}
          </div>
        ))}
      </div>

      {/* Role navigation visibility */}
      <section className="rounded-xl border border-border bg-card p-4 space-y-4" data-testid="role-navigation-settings">
        <div>
          <h2 className="font-semibold">Navigation menus</h2>
          <p className="text-sm text-muted-foreground">Select which menu items to display for each role. Selecting a menu grants its related permission when that role supports it. Role-specific page access remains enforced.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {roles.map((role) => {
            const available = new Set(data.available_navigation?.[role] || []);
            const selected = navigationFor(role);
            const adminGroup = (data.navigation_items || []).find((item) => item.id === "administration");
            const mainItems = (data.navigation_items || []).filter((item) => item.section === "main" && item.id !== "administration");
            const adminChildren = adminGroup?.children || [];
            const hasChanges = Boolean(edited[role]?.navigation);
            return (
              <div key={role} className="rounded-lg border border-border p-3 space-y-2" data-testid={`role-nav-card-${role}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[10px] uppercase tracking-widest text-primary">{role.replace(/_/g, " ")}</div>
                  {canEdit && hasChanges && (
                    <Button size="sm" variant="outline" data-testid={`role-nav-save-${role}`} onClick={() => save(role)} disabled={saving === role}>
                      <Save className="h-3 w-3 mr-1" /> Save
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                  {mainItems.map((item) => (
                    <label key={item.id} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" data-testid={`nav-${role}-${item.id}`} checked={selected.has(item.id)} disabled={!canEdit || !available.has(item.id)}
                        title={!available.has(item.id) ? "This menu is outside the role's current access" : undefined}
                        onChange={(event) => setNavigation(role, item.id, event.target.checked)} className="h-4 w-4 accent-primary" />
                      {item.label}
                    </label>
                  ))}
                </div>
                <div className="border-t border-border pt-2">
                  <div className="flex items-center gap-2">
                    <button type="button" aria-label={`${expandedMenus[role] ? "Collapse" : "Expand"} Administration menus`}
                      onClick={() => setExpandedMenus((prev) => ({ ...prev, [role]: !prev[role] }))} className="p-1 text-muted-foreground">
                      {expandedMenus[role] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <input type="checkbox" data-testid={`nav-${role}-administration`} checked={selected.has("administration")} disabled={!canEdit || adminChildren.every((item) => !available.has(item.id))}
                        title={adminChildren.every((item) => !available.has(item.id)) ? "Administration is outside this role's current access" : undefined}
                        onChange={(event) => setNavigation(role, "administration", event.target.checked)} className="h-4 w-4 accent-primary" />
                      Administration
                    </label>
                  </div>
                  {expandedMenus[role] && (
                    <div className="ml-8 mt-2 grid grid-cols-2 gap-x-3 gap-y-2">
                      {adminChildren.map((item) => (
                        <label key={item.id} className="flex items-center gap-2 text-sm">
                          <input type="checkbox" data-testid={`nav-${role}-${item.id}`} checked={selected.has(item.id)} disabled={!canEdit || !available.has(item.id)}
                            title={!available.has(item.id) ? "This menu is outside the role's current access" : undefined}
                            onChange={(event) => setNavigation(role, item.id, event.target.checked)} className="h-4 w-4 accent-primary" />
                          {item.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Permission matrix */}
      <div className="rounded-xl border border-border bg-card overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground sticky left-0 bg-card">Permission</th>
              {roles.map((r) => (
                <th key={r} className="px-3 py-3 text-[10px] uppercase tracking-widest text-muted-foreground text-center">
                  <div>{r.replace(/_/g, " ")}</div>
                  {canEdit && edited[r]?.permissions && (
                    <button data-testid={`role-perms-save-${r}`} onClick={() => save(r)} disabled={saving === r}
                      className="mt-1 text-[10px] normal-case tracking-normal text-primary hover:underline">
                      save
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.actions.map((a) => (
              <tr key={a.key} data-row data-testid={`perm-row-${a.key}`} className="border-b border-border/70">
                <td className="px-4 py-3 sticky left-0 bg-card">{a.label}</td>
                {roles.map((r) => {
                  const has = permsFor(r).has(a.key);
                  const editable =
                    canEdit &&
                    r !== ROLES.SUPER_ADMIN &&
                    (data.available?.[r] || []).includes(a.key);
                  return (
                    <td key={r} className="text-center py-3">
                      {editable ? (
                        <input
                          type="checkbox"
                          data-testid={`perm-${r}-${a.key}`}
                          checked={has}
                          onChange={(e) => setPerm(r, a.key, e.target.checked)}
                          className="h-4 w-4 accent-primary cursor-pointer"
                        />
                      ) : (
                        has ? <Check className="inline h-4 w-4 text-primary" /> : <span className="text-muted-foreground/40">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
